import {
  NOTIFICATION_KINDS,
  NOTIFICATION_STATUS,
  RAW_STATUS_MAP,
  STATUS_MESSAGES,
} from './notificationsConstants';

/**
 * Pulls the prefix out of a channel name.
 *
 * The backend publishes to `{prefix}_{role}_{userId}` and the socket server
 * re-emits under that same string, so the prefix is whatever is left after
 * removing our own suffix. Returns `null` for anything addressed to someone
 * else, which the reducer then drops.
 *
 * @param {string} channel - The socket.io event name.
 * @param {string} role
 * @param {number|string} userId
 * @returns {string|null}
 */
export const parseChannel = (channel, role, userId) => {
  if (typeof channel !== 'string') return null;

  const suffix = `_${role}_${userId}`;
  if (!channel.endsWith(suffix)) return null;

  return channel.slice(0, -suffix.length) || null;
};

/**
 * @param {unknown} rawStatus
 * @returns {string} One of `NOTIFICATION_STATUS`.
 */
export const normalizeStatus = (rawStatus) =>
  RAW_STATUS_MAP[String(rawStatus ?? '').toLowerCase()] ?? NOTIFICATION_STATUS.RUNNING;

/**
 * Turns one socket payload into the fields a panel entry needs.
 *
 * An unknown kind keeps its raw prefix as the title instead of being dropped or
 * relabelled: a task added on the backend should show up looking odd, not
 * disappear silently.
 *
 * @param {string} kind - The channel prefix.
 * @param {object} payload - The Django dict, verbatim.
 */
const toCount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

/**
 * `{total, done, failed}` when the payload counts its work, else `null`. The
 * upload says `processed` for what the newer contract calls `done`.
 */
const readProgress = (payload) => {
  const total = toCount(payload.total);
  if (!total) return null;
  return {
    total,
    done: toCount(payload.done ?? payload.processed) ?? 0,
    failed: toCount(payload.failed) ?? 0,
  };
};

export const describeEvent = (kind, payload = {}) => {
  const catalog = NOTIFICATION_KINDS[kind];
  const progress = readProgress(payload);
  let status = normalizeStatus(payload.status);

  // A success that counts failures is not the whole story: the upload reports
  // "completed" even when rows were left out.
  if (status === NOTIFICATION_STATUS.SUCCESS && progress?.failed > 0) {
    status = NOTIFICATION_STATUS.PARTIAL;
  }

  return {
    status,
    rawStatus: payload.status ?? null,
    title: catalog?.title ?? kind,
    // The backend's own sentence wins when it wrote one — it is more specific
    // than anything the catalog can say ("¡Zona Norte creada con 340 comunas!").
    message: payload.message || catalog?.[status] || STATUS_MESSAGES[status] || '',
    // Only the deliveries export sends this today: a presigned S3 link that
    // lives for 7 days. Reading it off the payload rather than off the catalog
    // means a task that starts sending one needs no change here.
    url: payload.url ?? null,
    progress,
  };
};

/**
 * The optimistic entry created the moment the user clicks, before the backend's
 * first event arrives.
 */
export const describeStart = (kind) => {
  const catalog = NOTIFICATION_KINDS[kind];

  return {
    status: NOTIFICATION_STATUS.RUNNING,
    rawStatus: null,
    title: catalog?.title ?? kind,
    message: catalog?.running ?? '',
    url: null,
    progress: null,
  };
};
